import React from 'react';
import { formatClockMmSsTenths } from '../../../../helpers/countdownClock';
import { EventPeriodEnum, MatchDetailVm } from '../../../../utils/api';
import { OvertimeUiMode } from '../../../match-report/match-clock/periodLabels';

interface ScoreHeaderProps {
    match: MatchDetailVm;
    period: EventPeriodEnum;
    overtimeMode: OvertimeUiMode;
    timeRemaining: number;
    shotClock: number;
    clockRunning: boolean;
    onEditClock: () => void;
    onEditShot: () => void;
    onSetPeriod: (period: EventPeriodEnum, overtimeMode?: OvertimeUiMode) => void;
}

interface PeriodDot {
    key: string;
    label: string;
    period: EventPeriodEnum;
    mode: OvertimeUiMode;
}

// Q1-Q4 plus PS (penalty shootout = our Overtime + penalty_shootout mode).
const PERIOD_DOTS: PeriodDot[] = [
    { key: 'q1', label: 'Q1', period: EventPeriodEnum.First_Period, mode: 'play' },
    { key: 'q2', label: 'Q2', period: EventPeriodEnum.Second_Period, mode: 'play' },
    { key: 'q3', label: 'Q3', period: EventPeriodEnum.Third_Period, mode: 'play' },
    { key: 'q4', label: 'Q4', period: EventPeriodEnum.Fourth_Period, mode: 'play' },
    { key: 'ps', label: 'PS', period: EventPeriodEnum.Overtime, mode: 'penalty_shootout' },
];

const formatClock = (totalSeconds: number): string => formatClockMmSsTenths(totalSeconds);

const abbr = (name?: string): string => {
    const letters = (name ?? '').replace(/[^A-Za-z]/g, '');
    return letters.slice(0, 3).toUpperCase() || '---';
};

const ScoreHeader: React.FC<ScoreHeaderProps> = ({
    match,
    period,
    overtimeMode,
    timeRemaining,
    shotClock,
    clockRunning,
    onEditClock,
    onEditShot,
    onSetPeriod,
}) => {
    const isActive = (dot: PeriodDot): boolean =>
        dot.period === period &&
        (dot.period !== EventPeriodEnum.Overtime || dot.mode === overtimeMode);

    return (
        <div className="m-sb">
            <div className="m-sb__top">
                <span className="m-sb__abbr">{abbr(match.homeTeamDisplayName)}</span>
                <div className="m-sb__periods">
                    {PERIOD_DOTS.map((dot) => (
                        <button
                            key={dot.key}
                            type="button"
                            className={`m-sb__dot${isActive(dot) ? ' is-active' : ''}`}
                            onClick={(): void => onSetPeriod(dot.period, dot.mode)}
                        >
                            {dot.label}
                        </button>
                    ))}
                </div>
                <span className="m-sb__abbr">{abbr(match.awayTeamDisplayName)}</span>
            </div>

            <div className="m-sb__mid">
                <span className="m-sb__score">{match.homeTeamGoalsTotal ?? 0}</span>
                <button
                    type="button"
                    className={`m-sb__clock${clockRunning ? ' is-running' : ' is-stopped'}`}
                    onClick={onEditClock}
                >
                    {formatClock(timeRemaining)}
                </button>
                <span className="m-sb__score">{match.awayTeamGoalsTotal ?? 0}</span>
            </div>

            <button type="button" className="m-sb__shot" onClick={onEditShot}>
                <span className="m-sb__shot-label">SHOT CLOCK</span>
                <span className="m-sb__shot-value">{Math.ceil(shotClock)}</span>
            </button>
        </div>
    );
};

export default ScoreHeader;
